import type { JourneySampleInput } from './data/journey-api';
import type { JourneyAuthoritySnapshot } from './data/journey-api';
import type { JourneyMapPosition } from './explore/codex-map';

export const JOURNEY_BATCH_SIZE = 15;
export const JOURNEY_MAX_PENDING_SAMPLES = 50;
export const JOURNEY_FLUSH_INTERVAL_MS = 15_000;
export const JOURNEY_MAX_PRESENTATION_POSITIONS = 24;
export const JOURNEY_UPLOAD_TIMEOUT_MS = 20_000;

function unknownSpeedProofReady(samples: readonly JourneySampleInput[]): boolean {
  if (samples.some((sample) => sample.speed_mps !== null)) return true;
  const first = samples[0];
  const last = samples.at(-1);
  if (!first || !last) return false;
  const duration = Date.parse(last.recorded_at) - Date.parse(first.recorded_at);
  // A null native speed is not a zero speed. Keep a bounded observation window
  // instead of repeatedly draining tiny batches that can never prove a stop.
  const upperDisplacement = sampleDistance(first, last) + first.accuracy_m + last.accuracy_m;
  return duration >= Math.min(60_000, Math.max(3_000, (upperDisplacement / 0.75 + 2) * 1_000));
}

function sampleDistance(first: JourneySampleInput, last: JourneySampleInput): number {
  const radians = Math.PI / 180;
  const latitude = (last.latitude - first.latitude) * radians;
  const longitude = (last.longitude - first.longitude) * radians;
  const haversine =
    Math.sin(latitude / 2) ** 2 +
    Math.cos(first.latitude * radians) *
      Math.cos(last.latitude * radians) *
      Math.sin(longitude / 2) ** 2;
  return 6_371_000 * 2 * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

function urgentUnknownSpeedUpload(samples: readonly JourneySampleInput[]): boolean {
  const first = samples[0];
  const last = samples.at(-1);
  if (!first || !last) return false;
  const seconds = (Date.parse(last.recorded_at) - Date.parse(first.recorded_at)) / 1_000;
  const distance = sampleDistance(first, last);
  // This only expedites server validation; it never decides movement eligibility.
  return seconds > 0 && (distance - first.accuracy_m - last.accuracy_m) / seconds >= 5;
}

interface ForegroundPositionTrackerOptions {
  geolocation: Pick<Geolocation, 'watchPosition' | 'clearWatch'>;
  onPosition: (position: JourneyMapPosition) => void;
  onError: (message: string) => void;
  document?: Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'>;
}

interface ForegroundJourneyTrackerOptions {
  geolocation: Pick<Geolocation, 'watchPosition' | 'clearWatch'>;
  initialPosition?: GeolocationPosition;
  journey: Pick<JourneyAuthoritySnapshot, 'id' | 'nonce' | 'nextSequenceNo' | 'startedAt'>;
  submit: (
    journeyId: string,
    nonce: string,
    sequenceNo: number,
    samples: readonly JourneySampleInput[],
    focusDiscoveryId?: string,
    signal?: AbortSignal,
  ) => Promise<JourneyAuthoritySnapshot>;
  onAuthority: (journey: JourneyAuthoritySnapshot) => void;
  onError: (message: string) => void;
  onRecoveryRequired?: (message: string) => void;
  onPosition?: (position: JourneyMapPosition) => void;
  batchSize?: number;
  maxPendingSamples?: number;
  flushIntervalMs?: number;
  document?: Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'>;
}

export function appendJourneyPresentationPosition(
  route: readonly JourneyMapPosition[],
  position: JourneyMapPosition,
  maximum = JOURNEY_MAX_PRESENTATION_POSITIONS,
): readonly JourneyMapPosition[] {
  return [...route, position].slice(-Math.max(1, maximum));
}

export interface ForegroundJourneyTracker {
  start: () => void;
  stop: () => Promise<void>;
  discard: () => Promise<void>;
  flush: (focusDiscoveryId?: string) => Promise<boolean>;
  verify: (
    position: GeolocationPosition,
    focusDiscoveryId: string,
    signal?: AbortSignal,
  ) => Promise<boolean>;
  pendingSamples: () => number;
}

export interface ForegroundPositionTracker {
  start: () => void;
  stop: () => void;
}

export function mapPositionFromGeolocation(position: GeolocationPosition): JourneyMapPosition {
  return {
    accuracyM: Math.max(0.1, Math.min(1_000, position.coords.accuracy)),
    coordinates: [position.coords.longitude, position.coords.latitude],
    recordedAt: new Date(position.timestamp).toISOString(),
  };
}

export function requestInitialForegroundPosition(
  geolocation: Pick<Geolocation, 'getCurrentPosition'>,
): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    geolocation.getCurrentPosition(
      resolve,
      (error) => {
        const explanation =
          error.code === error.PERMISSION_DENIED
            ? 'Location permission is off. Use Daily Codex, or allow location before starting a Journey.'
            : error.code === error.TIMEOUT
              ? 'No reliable GPS position arrived in time. Use Daily Codex and try Journey again outdoors.'
              : 'This device could not provide a reliable GPS position. Use Daily Codex instead.';
        reject(new Error(explanation));
      },
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 12_000 },
    );
  });
}

export function requestFreshForegroundPosition(
  geolocation: Pick<Geolocation, 'getCurrentPosition'>,
  signal?: AbortSignal,
): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const cleanup = () => {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', cancel);
    };
    const cancel = () => {
      cleanup();
      reject(signal?.reason);
    };
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('No fresh GPS fix arrived. Keep Explore visible and try again outdoors.'));
    }, 16_000);
    signal?.addEventListener('abort', cancel, { once: true });
    geolocation.getCurrentPosition(
      (position) => {
        cleanup();
        resolve(position);
      },
      (error) => {
        cleanup();
        const explanation =
          error.code === error.PERMISSION_DENIED
            ? 'Location permission is off. Allow precise location to verify this stop.'
            : error.code === error.TIMEOUT
              ? 'No fresh GPS fix arrived. Keep Explore visible and try again outdoors.'
              : 'This device could not refresh its GPS position. Try again with Explore visible.';
        reject(new Error(explanation));
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15_000 },
    );
  });
}

/**
 * Keep the presentation marker attached to real device GPS while Explore is visible.
 *
 * These fixes are volatile and never submitted for eligibility or reward decisions. An
 * authoritative Journey owns its own bounded, server-verified sample tracker instead.
 */
export function createForegroundPositionTracker(
  options: ForegroundPositionTrackerOptions,
): ForegroundPositionTracker {
  let watchId: number | undefined;
  let stopped = false;

  function clearWatch() {
    if (watchId !== undefined) options.geolocation.clearWatch(watchId);
    watchId = undefined;
  }

  function beginWatch() {
    if (stopped || watchId !== undefined || options.document?.visibilityState === 'hidden') return;
    watchId = options.geolocation.watchPosition(
      (position) => {
        if (!stopped) options.onPosition(mapPositionFromGeolocation(position));
      },
      (error) => {
        const explanation =
          error.code === error.PERMISSION_DENIED
            ? 'Live GPS permission was removed. Allow location to keep your avatar on your streets.'
            : error.code === error.TIMEOUT
              ? 'Live GPS is taking longer than expected. Keep Explore visible and try moving outdoors.'
              : 'Live GPS temporarily lost its fix. Your last foreground position remains visible.';
        options.onError(explanation);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15_000 },
    );
  }

  function visibilityChanged() {
    if (options.document?.visibilityState === 'hidden') clearWatch();
    else beginWatch();
  }

  return {
    start() {
      if (stopped) return;
      beginWatch();
      options.document?.addEventListener('visibilitychange', visibilityChanged);
    },
    stop() {
      if (stopped) return;
      stopped = true;
      clearWatch();
      options.document?.removeEventListener('visibilitychange', visibilityChanged);
    },
  };
}

export function createForegroundJourneyTracker(
  options: ForegroundJourneyTrackerOptions,
): ForegroundJourneyTracker {
  const batchSize = Math.max(
    1,
    Math.min(JOURNEY_BATCH_SIZE, options.batchSize ?? JOURNEY_BATCH_SIZE),
  );
  const maxPendingSamples = Math.max(
    batchSize,
    Math.min(JOURNEY_MAX_PENDING_SAMPLES, options.maxPendingSamples ?? JOURNEY_MAX_PENDING_SAMPLES),
  );
  const flushIntervalMs = Math.max(
    1_000,
    Math.min(JOURNEY_FLUSH_INTERVAL_MS, options.flushIntervalMs ?? JOURNEY_FLUSH_INTERVAL_MS),
  );
  let watchId: number | undefined;
  let intervalId: ReturnType<typeof setInterval> | undefined;
  let sequenceNo = options.journey.nextSequenceNo;
  let lastRecordedAt = 0;
  let stopped = false;
  let discarded = false;
  let pending: JourneySampleInput[] = [];
  let inFlight: Promise<boolean> | undefined;
  let uploadController: AbortController | undefined;
  let verifying = false;
  let focusedSampleCount = 0;
  let retryRequest: { samples: JourneySampleInput[]; sequence: number; focus?: string } | undefined;

  function sampleFromPosition(position: GeolocationPosition): JourneySampleInput | undefined {
    const timestamp = Math.trunc(position.timestamp);
    const startedAt = options.journey.startedAt ? Date.parse(options.journey.startedAt) : 0;
    // Android can deliver the same native fix through watchPosition and a fresh
    // request. Never manufacture a later time for that old observation: even a
    // tiny GPS displacement would then imply an enormous speed.
    if (
      !Number.isFinite(timestamp) ||
      timestamp <= lastRecordedAt ||
      (Number.isFinite(startedAt) && timestamp < startedAt - 2_000)
    )
      return undefined;
    if (position.coords.speed === null && lastRecordedAt > 0 && timestamp - lastRecordedAt < 2_000)
      return undefined;
    lastRecordedAt = timestamp;
    const speed = position.coords.speed;
    return {
      longitude: position.coords.longitude,
      latitude: position.coords.latitude,
      accuracy_m: Math.max(0.1, Math.min(1_000, position.coords.accuracy)),
      recorded_at: new Date(timestamp).toISOString(),
      speed_mps: speed !== null && Number.isFinite(speed) ? Math.max(0, speed) : null,
    };
  }

  async function flush(focusDiscoveryId?: string, signal?: AbortSignal): Promise<boolean> {
    if (discarded || signal?.aborted || options.document?.visibilityState === 'hidden')
      return false;
    if (inFlight) return inFlight;
    if (!retryRequest && pending.length === 0) return true;
    if (
      !retryRequest &&
      !stopped &&
      !focusDiscoveryId &&
      !unknownSpeedProofReady(pending) &&
      !urgentUnknownSpeedUpload(pending)
    )
      return true;
    retryRequest ??= {
      samples: pending.splice(
        0,
        pending.every((sample) => sample.speed_mps === null) ? maxPendingSamples : batchSize,
      ),
      sequence: sequenceNo,
      focus: focusDiscoveryId,
    };
    const request = retryRequest;
    const controller = new AbortController();
    uploadController = controller;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let cancel: () => void = () => undefined;
    const deadline = new Promise<never>((_resolve, reject) => {
      cancel = () => {
        controller.abort(signal?.reason);
        reject(signal?.reason ?? new DOMException('Location check cancelled', 'AbortError'));
      };
      signal?.addEventListener('abort', cancel, { once: true });
      timeout = setTimeout(() => {
        controller.abort();
        reject(new Error('Location check timed out. Keep Explore open; we will retry safely.'));
      }, JOURNEY_UPLOAD_TIMEOUT_MS);
    });
    let succeeded = false;
    const submit = () => {
      try {
        return options.submit(
          options.journey.id,
          options.journey.nonce,
          request.sequence,
          request.samples,
          request.focus,
          controller.signal,
        );
      } catch (error) {
        return Promise.reject(error);
      }
    };
    inFlight = Promise.race([submit(), deadline])
      .then((authority) => {
        sequenceNo = Math.max(request.sequence + 1, authority.nextSequenceNo);
        retryRequest = undefined;
        succeeded = true;
        if (!discarded) options.onAuthority(authority);
        return true;
      })
      .catch((error: unknown) => {
        if (!discarded && !signal?.aborted) {
          const code =
            typeof error === 'object' && error !== null && 'code' in error ? error.code : undefined;
          if (
            typeof code === 'string' &&
            [
              'journey_timestamp_invalid',
              'journey_samples_overlap',
              'journey_sequence_invalid',
              'journey_not_active',
              'idempotency_key_reused',
            ].includes(code)
          ) {
            discarded = true;
            pending = [];
            retryRequest = undefined;
            closeTracking();
            const message =
              'Location verification needs a fresh Journey. Stop safely, then restart Journey.';
            options.onError(message);
            options.onRecoveryRequired?.(message);
          } else {
            options.onError(
              error instanceof Error
                ? error.message
                : 'Location check interrupted. We will retry safely.',
            );
          }
        }
        return false;
      })
      .finally(() => {
        inFlight = undefined;
        if (timeout !== undefined) clearTimeout(timeout);
        signal?.removeEventListener('abort', cancel);
        if (uploadController === controller) uploadController = undefined;
        if (succeeded && pending.length >= batchSize && !stopped && !verifying) void flush();
      });
    return inFlight;
  }

  function clearWatch() {
    if (watchId !== undefined) options.geolocation.clearWatch(watchId);
    watchId = undefined;
  }

  function beginWatch() {
    if (stopped || watchId !== undefined || options.document?.visibilityState === 'hidden') return;
    watchId = options.geolocation.watchPosition(
      (position) => {
        if (stopped) return;
        const sample = sampleFromPosition(position);
        if (!sample) return;
        const pendingCapacity = Math.max(
          0,
          maxPendingSamples - (retryRequest?.samples.length ?? 0) - focusedSampleCount,
        );
        if (pending.length >= pendingCapacity) {
          pending.shift();
          options.onError('Location check interrupted. Keep Explore open; we will retry safely.');
        }
        if (pendingCapacity > 0) pending.push(sample);
        options.onPosition?.({
          accuracyM: sample.accuracy_m,
          coordinates: [sample.longitude, sample.latitude],
          recordedAt: sample.recorded_at,
        });
        if (pending.length >= batchSize && !verifying && !retryRequest) void flush();
      },
      (error) => {
        const explanation =
          error.code === error.PERMISSION_DENIED
            ? 'Location permission is off. Journey recording needs foreground location access.'
            : 'The browser could not provide a reliable foreground location.';
        options.onError(explanation);
      },
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 20_000 },
    );
  }

  function visibilityChanged() {
    if (options.document?.visibilityState === 'hidden') {
      discarded = true;
      pending = [];
      retryRequest = undefined;
      uploadController?.abort();
      closeTracking();
    }
  }

  function closeTracking() {
    stopped = true;
    clearWatch();
    if (intervalId !== undefined) clearInterval(intervalId);
    options.document?.removeEventListener('visibilitychange', visibilityChanged);
  }

  return {
    start() {
      if (stopped) return;
      if (options.initialPosition) {
        const sample = sampleFromPosition(options.initialPosition);
        if (sample) pending.push(sample);
        options.onPosition?.({
          ...mapPositionFromGeolocation(options.initialPosition),
        });
      }
      beginWatch();
      intervalId ??= setInterval(() => {
        if (!verifying) void flush();
      }, flushIntervalMs);
      options.document?.addEventListener('visibilitychange', visibilityChanged);
    },
    async stop() {
      closeTracking();
      if (inFlight && !(await inFlight)) {
        throw new Error('Your walk could not be saved. Reconnect and try finishing again.');
      }
      const finalBatchLimit = Math.ceil(maxPendingSamples / batchSize) + 1;
      for (
        let attempt = 0;
        attempt < finalBatchLimit && (retryRequest || pending.length > 0);
        attempt += 1
      ) {
        if (!(await flush())) {
          throw new Error('Your walk could not be saved. Reconnect and try finishing again.');
        }
      }
      if (retryRequest || pending.length > 0) {
        throw new Error('Your walk is still saving. Try finishing again.');
      }
      uploadController?.abort();
    },
    async discard() {
      discarded = true;
      pending = [];
      retryRequest = undefined;
      focusedSampleCount = 0;
      uploadController?.abort();
      closeTracking();
    },
    flush,
    async verify(position, focusDiscoveryId, signal) {
      if (stopped || verifying || signal?.aborted) return false;
      if ((retryRequest?.samples.length ?? 0) >= maxPendingSamples) return false;
      const sample = sampleFromPosition(position);
      if (!sample) return false;
      if (
        !unknownSpeedProofReady([...pending, sample]) &&
        !urgentUnknownSpeedUpload([...pending, sample])
      ) {
        pending = [...pending, sample].slice(-maxPendingSamples);
        options.onPosition?.(mapPositionFromGeolocation(position));
        return false;
      }
      verifying = true;
      const focusedLimit = Math.max(
        1,
        Math.min(
          [...pending, sample].every((fix) => fix.speed_mps === null)
            ? maxPendingSamples
            : batchSize,
          maxPendingSamples - (retryRequest?.samples.length ?? 0),
        ),
      );
      const focusedSamples = [...pending, sample].slice(-focusedLimit);
      focusedSampleCount = focusedSamples.length;
      pending = [];
      options.onPosition?.({
        accuracyM: sample.accuracy_m,
        coordinates: [sample.longitude, sample.latitude],
        recordedAt: sample.recorded_at,
      });
      let submittedFocused = false;
      try {
        if (inFlight && !(await inFlight)) return false;
        if (stopped || signal?.aborted) return false;
        if (retryRequest && !(await flush())) return false;
        if (stopped || signal?.aborted) return false;
        retryRequest = { samples: focusedSamples, sequence: sequenceNo, focus: focusDiscoveryId };
        submittedFocused = true;
        focusedSampleCount = 0;
        return await flush(undefined, signal);
      } finally {
        // Preserve unsent foreground samples when selection closes while waiting
        // for the preceding immutable batch; do not manufacture a focused visit.
        if (!submittedFocused && !discarded)
          pending = [...focusedSamples, ...pending].slice(-maxPendingSamples);
        verifying = false;
        focusedSampleCount = 0;
      }
    },
    pendingSamples: () => pending.length + (retryRequest?.samples.length ?? 0) + focusedSampleCount,
  };
}
